/**
 * Calendar tracking utilities for RBC flights
 * Provides comprehensive event properties for viewed_calendar event
 */

import { useMemo } from "react";
import type { ITripTerminus, IPassengerCounts } from "@b2bportal/core-types";
import type { TripCategory } from "@overrides/types";
import { useGetCurrentFunnel } from "../hooks";

export interface ViewedCalendarProperties {
  destination?: string;
  destination_label?: string;
  destination_region_code?: string;
  lob: string;
  origin?: string;
  origin_label?: string;
  origin_region_code?: string;
  searched_pax_total?: number;
  searched_pax_total_infant_lap?: number;
  trip_type?: TripCategory;
}

/**
 * Hook to build comprehensive calendar tracking properties
 */
export const useViewedCalendarProperties = (
  origin?: ITripTerminus,
  destination?: ITripTerminus,
  tripCategory?: TripCategory,
  passengerCounts?: IPassengerCounts
): ViewedCalendarProperties => {
  const funnel = useGetCurrentFunnel();

  return useMemo(() => {
    const originCode = origin?.id.code.code;
    const destinationCode = destination?.id.code.code;

    const totalPax = passengerCounts
      ? passengerCounts.adultsCount +
        passengerCounts.childrenCount +
        passengerCounts.infantsInSeatCount +
        passengerCounts.infantsOnLapCount
      : undefined;

    const infantLapCount = passengerCounts?.infantsOnLapCount;

    return {
      destination: destinationCode
        ? `${destination?.id.code.regionType}/${destinationCode}`
        : undefined,
      destination_label: destination?.label,
      destination_region_code: destinationCode,
      lob: funnel,
      origin: originCode
        ? `${origin?.id.code.regionType}/${originCode}`
        : undefined,
      origin_label: origin?.label,
      origin_region_code: originCode,
      searched_pax_total: totalPax,
      searched_pax_total_infant_lap: infantLapCount,
      trip_type: tripCategory,
    };
  }, [
    origin?.id.code.code,
    origin?.id.code.regionType,
    origin?.label,
    destination?.id.code.code,
    destination?.id.code.regionType,
    destination?.label,
    passengerCounts?.adultsCount,
    passengerCounts?.childrenCount,
    passengerCounts?.infantsInSeatCount,
    passengerCounts?.infantsOnLapCount,
    funnel,
    tripCategory,
  ]);
};
